import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * How the management interview is held.
 *
 * Which of these a member may actually pick is **not** decided here — it is
 * whatever staff published windows for (D9a), and the gender-scoped
 * `verification.interviewMethods` setting records the operator's policy on what
 * to publish per audience (U15), because operators commonly offer women a
 * video call and men a phone call.
 */
export const INTERVIEW_METHODS = ['video', 'phone', 'in_person'] as const;

export const InterviewMethodSchema = z.enum(INTERVIEW_METHODS);

export type InterviewMethod = (typeof INTERVIEW_METHODS)[number];

/**
 * Whether a window or an appointment held this way must carry a place-or-link.
 * A video call without its link is an appointment nobody can attend; a phone
 * call has no place to be, and an address may follow (owner directive 2026-09-16).
 */
export const requiresLocationOrLink = (method: InterviewMethod): boolean => method === 'video';

export const LOCATION_REQUIRED_MESSAGE = 'A video call needs its meeting link.';

/**
 * What staff record after conducting it off-platform.
 *
 * `rescheduled` is an outcome rather than a deletion: the appointment happened
 * to the extent that somebody turned up to move it, and a member who was
 * rescheduled twice is a fact a reviewer should be able to see.
 */
export const INTERVIEW_OUTCOMES = ['passed', 'failed', 'no_show', 'rescheduled'] as const;

export const InterviewOutcomeSchema = z.enum(INTERVIEW_OUTCOMES);

export type InterviewOutcome = (typeof INTERVIEW_OUTCOMES)[number];

/**
 * A member booking one of the windows staff published (D9a).
 *
 * Only the slot id: the method, the time and the place are the **slot's**, so a
 * body that repeated them would be a second opinion the server would have to
 * trust or ignore. Booking again with a different slot is a reschedule.
 */
export const BookInterviewSlotSchema = z
  .object({
    slotId: UuidSchema,
  })
  .strict();

export type BookInterviewSlotRequest = z.infer<typeof BookInterviewSlotSchema>;

/** Staff confirming the appointment — the instant, the method and where to go. */
export const ScheduleInterviewSchema = z
  .object({
    method: InterviewMethodSchema,
    scheduledAt: z.iso.datetime({ offset: true }),
    // Trimmed to a non-empty string or explicitly absent: a phone call has no
    // place to be, but whitespace is not a venue.
    locationOrLink: z.string().trim().min(1).max(500).nullable(),
  })
  .strict()
  .refine(
    (schedule) => !requiresLocationOrLink(schedule.method) || schedule.locationOrLink !== null,
    {
      message: LOCATION_REQUIRED_MESSAGE,
      path: ['locationOrLink'],
    },
  );

export type ScheduleInterviewRequest = z.infer<typeof ScheduleInterviewSchema>;

/** Staff recording what happened. `notes` stay staff-side — see {@link InterviewSchema}. */
export const RecordInterviewOutcomeSchema = z
  .object({
    outcome: InterviewOutcomeSchema,
    notes: z.string().trim().min(1).max(2000).nullable(),
  })
  .strict();

export type RecordInterviewOutcomeRequest = z.infer<typeof RecordInterviewOutcomeSchema>;

/**
 * The appointment as **the member** reads it, and as the staff case detail
 * embeds it.
 *
 * `notes` is absent by construction, not stripped by a mapper that could forget:
 * it is the reviewer's private record of the conversation, and this is the shape
 * a member's own screen renders.
 */
export const InterviewSchema = z
  .object({
    id: UuidSchema,
    method: InterviewMethodSchema,
    requestedAt: z.iso.datetime(),
    preferredAt: z.iso.datetime(),
    /**
     * Null until confirmed — "requested" and "booked" are different states. A
     * booking made from a published slot is confirmed at once, so it is set
     * from the start; a staff-set appointment is null until `schedule` runs.
     */
    scheduledAt: z.iso.datetime().nullable(),
    locationOrLink: z.string().nullable(),
    outcome: InterviewOutcomeSchema.nullable(),
    /** The published window this was booked into; `null` for a staff-set or legacy appointment. */
    slotId: UuidSchema.nullable(),
    /** `true` when the member booked it from availability — the appointment nobody had to confirm. */
    bookedFromSlot: z.boolean(),
  })
  .strict();

export type Interview = z.infer<typeof InterviewSchema>;
